import React from "react";
import { productsData } from "../components/ListOfProjects";
import appStoreBadge from "./images/download-on-the-app-store-apple-logo-svgrepo-com.svg";
import googlePlayBadge from "./images/google-play-badge-logo-svgrepo-com.svg";
import "./styles/MyProjects.css";
import { Eyebrow, Reveal } from "./motion";

const projectMeta = [
  {
    category: "Mobile · Business Intelligence",
    status: "Live",
    tags: ["React Native", "Firebase", "Charts", "Realtime"],
  },
  {
    category: "Mobile · Audio & UI/UX",
    status: "In development",
    tags: ["React Native", "UI/UX", "Multi-device", "Streaming"],
  },
  {
    category: "Mobile · Civic Tech",
    status: "Awaiting approval",
    tags: ["React Native", "Maps", "Push notifications", "Realtime"],
  },
];

const statusStyles = {
  Live: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  "Awaiting approval": "bg-primary-500/10 text-primary-400 border-primary-500/30",
  "In development": "bg-amber-500/10 text-amber-400 border-amber-500/30",
};

function ProjectCard({ product, meta, index }) {
  const flip = index % 2 === 1;

  return (
    <article className="group relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm shadow-xl transition-all duration-500 hover:-translate-y-1.5 hover:border-primary-500/25 hover:shadow-2xl">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-48 w-[80%] -translate-x-1/2 rounded-full bg-primary-500/12 opacity-0 blur-[80px] transition-opacity duration-700 group-hover:opacity-100"
      />

      <div
        className={`grid gap-0 lg:grid-cols-2 ${
          flip ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        {/* preview */}
        <div className="relative overflow-hidden p-6 sm:p-8 lg:p-10">
          <div
            aria-hidden
            className="absolute inset-0 opacity-70"
            style={{
              background:
                "radial-gradient(ellipse at 50% 20%, rgba(14,165,233,0.12), transparent 65%)",
            }}
          />
          <img
            src={product.image}
            alt={`${product.title} app preview`}
            loading="lazy"
            className="relative mx-auto w-full max-w-md object-contain drop-shadow-2xl transition-transform duration-700 group-hover:scale-[1.04]"
            style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
          />
        </div>

        {/* content */}
        <div className="flex flex-col justify-center border-t border-slate-800 p-6 sm:p-8 lg:border-t-0 lg:p-12">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <span className="truncate font-mono text-[11px] tracking-[0.2em] text-primary-400 uppercase">
              {meta.category}
            </span>
            <span
              className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider border ${
                statusStyles[meta.status] || statusStyles["In development"]
              }`}
            >
              {meta.status}
            </span>
          </div>

          <h3 className="mt-4 text-[clamp(1.6rem,3vw,2.4rem)] font-bold leading-tight text-slate-100 group-hover:text-primary-400 transition-colors duration-300 font-display">
            {product.title}
          </h3>

          <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">
            {product.description}
          </p>

          <ul className="mt-6 flex flex-wrap gap-2">
            {meta.tags.map((tag) => (
              <li
                key={tag}
                className="rounded-lg border border-slate-800 bg-slate-800/60 px-3 py-1.5 font-mono text-[11px] tracking-wide text-slate-400 transition-colors duration-300 group-hover:border-primary-500/20"
              >
                {tag}
              </li>
            ))}
          </ul>

          {index === 0 ? (
            <div className="mt-8 flex flex-row flex-wrap items-center justify-center gap-3 lg:justify-start">
              <a
                href="https://apps.apple.com/ba/app/real-time-analytics/id6474943267?l=hr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-all duration-300 hover:-translate-y-0.5 hover:opacity-80"
              >
                <img
                  src={appStoreBadge}
                  alt="Download on the App Store"
                  className="h-28 w-auto"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.mirzaherenda.garsontraffic"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-all duration-300 hover:-translate-y-0.5 hover:opacity-80"
              >
                <img
                  src={googlePlayBadge}
                  alt="Get it on Google Play"
                  className="h-28 w-auto"
                />
              </a>
            </div>
          ) : meta.status === "Awaiting approval" ? (
            <div className="mt-8 flex flex-col gap-4 rounded-xl border border-primary-500/20 bg-primary-500/5 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary-400"></span>
                </span>
                <div>
                  <span className="block text-sm font-bold text-primary-400">
                    Completed &mdash; awaiting store approval
                  </span>
                  <span className="text-xs text-slate-500">
                    The app is finished and currently under review
                  </span>
                </div>
              </div>
              {product.link && (
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-400"
                >
                  More info &rarr;
                </a>
              )}
            </div>
          ) : (
            <div className="mt-8 flex items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 px-5 py-4">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400"></span>
              </span>
              <div>
                <span className="block text-sm font-bold text-amber-400">
                  Currently in development
                </span>
                <span className="text-xs text-slate-500">
                  Not yet available &mdash; check back soon
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function MyProjects() {
  return (
    <section
      id="products"
      className="relative bg-dark-bg px-4 py-24 sm:px-6 lg:py-32"
    >
      <div className="mx-auto max-w-6xl 2xl:max-w-7xl">
        <Reveal>
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <Eyebrow>Selected work</Eyebrow>
              <h2 className="mt-6 max-w-2xl text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-[1.05] text-transparent bg-gradient-to-r from-primary-400 to-sky-400 bg-clip-text font-display">
                Products built to solve real problems.
              </h2>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-slate-400">
              Three mobile products — one live on both stores, one awaiting
              store approval, and one in active development.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 flex flex-col gap-6 lg:gap-8">
          {productsData.map((product, index) => {
            const meta =
              projectMeta[index] || projectMeta[projectMeta.length - 1];
            return (
              <Reveal key={product.id} delay={index * 160}>
                <ProjectCard product={product} meta={meta} index={index} />
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default MyProjects;
